import { GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';

import { OrderDirection } from '@/types/schema';

export interface OrderType<TField = string> {
  field: TField;
  direction?: OrderDirection;
}

export interface UseDataGridSortParams<TOrderField extends string> {
  /**
   * Mapping from DataGrid column field names to GraphQL order field enums.
   * IMPORTANT: Pass a memoized or constant object to avoid handler re-creation.
   */
  columnFieldToSortField: Record<string, TOrderField>;
  defaultSort?: OrderType<TOrderField>[];
}

/**
 * Generic hook for handling DataGrid sort state and conversion to GraphQL order types.
 * Prevents the sorting cycle bug where users get stuck in descending order.
 *
 * Note: Only the first column sort is honored if multi-column sorting is enabled.
 */
export function useDataGridSort<TOrderField extends string>({
  columnFieldToSortField,
  defaultSort = [],
}: UseDataGridSortParams<TOrderField>) {
  const [orderBy, setOrderBy] = useState<OrderType<TOrderField>[]>(defaultSort);

  // Helper to get effective orderBy (falls back to default when empty)
  const getEffectiveOrderBy = useCallback(() => {
    return orderBy.length > 0 ? orderBy : defaultSort;
  }, [orderBy, defaultSort]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      let newOrderBy: OrderType<TOrderField>[];

      if (model.length === 0) {
        // Set to empty array when sort is cleared by user
        newOrderBy = [];
      } else {
        const sortItem = model[0];
        if (!sortItem) {
          // Set to empty array if sortItem is somehow null/undefined
          newOrderBy = [];
        } else {
          const { field, sort } = sortItem;
          const mappedField = columnFieldToSortField[field];

          if (mappedField && sort) {
            const direction =
              sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
            newOrderBy = [
              { field: mappedField, direction },
            ] as OrderType<TOrderField>[];
          } else {
            // Fallback to empty array if mapping fails or sort is missing
            newOrderBy = [];
          }
        }
      }

      // Only update if the new orderBy is different to prevent redundant renders
      if (JSON.stringify(newOrderBy) !== JSON.stringify(orderBy)) {
        setOrderBy(newOrderBy);
      }
    },
    [columnFieldToSortField, orderBy]
  );

  return { orderBy, handleSortModelChange, getEffectiveOrderBy };
}
